import React from 'react';
import { motion } from 'framer-motion';
import { useLanguageStore } from '../../store/languageStore';
import { useTranslation } from 'react-i18next';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'text' | 'gradient' | 'success' | 'warning' | 'info' | 'light';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  fullWidth?: boolean;
  style?: React.CSSProperties;
  title?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  children,
  onClick,
  type = 'button',
  className = '',
  fullWidth = false,
  style: styleProp,
  title,
}) => {
  const { isRTL } = useLanguageStore();
  const { t } = useTranslation();
  
  // Bootstrap classes
  const bootstrapVariants: Record<string, string> = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    danger: 'btn-danger',
    success: 'btn-success',
    warning: 'btn-warning',
    info: 'btn-info',
    light: 'btn-light',
    text: 'btn-link',
    gradient: 'btn-primary', // Bootstrap doesn't have gradient, we'll add custom
  };
  
  const bootstrapSizes: Record<string, string> = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg',
  };
  
  const baseClasses = `btn btn-modern ${bootstrapVariants[variant]} ${bootstrapSizes[size]} position-relative overflow-hidden`;
  const disabledClasses = disabled || isLoading ? 'disabled opacity-50' : '';
  const widthClasses = fullWidth ? 'w-100' : '';
  const iconSpacing = isRTL ? 'ms-2' : 'me-2';
  const gradientClass = variant === 'gradient' ? 'gradient-primary text-white' : '';

  // Enhanced button styles
  const buttonStyle: React.CSSProperties = {
    fontWeight: 500,
    letterSpacing: '0.01em',
    transition: 'all 200ms ease-in-out',
    ...styleProp,
  };

  if (variant === 'primary') {
    buttonStyle.background = 'linear-gradient(135deg, var(--color-primary-600) 0%, var(--color-primary-500) 100%)';
    buttonStyle.color = 'white';
    buttonStyle.border = 'none';
    buttonStyle.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.3)';
  } else if (variant === 'success') {
    buttonStyle.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    buttonStyle.color = 'white';
    buttonStyle.border = 'none';
    buttonStyle.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
  }

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`${baseClasses} ${disabledClasses} ${widthClasses} ${gradientClass} ${className}`}
      style={buttonStyle}
      title={title}
      whileHover={!disabled && !isLoading ? { scale: 1.02, y: -1 } : {}}
      whileTap={!disabled && !isLoading ? { scale: 0.98, y: 0 } : {}}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Ripple effect background */}
      {variant !== 'text' && (
        <span className="position-absolute top-0 start-0 w-100 h-100 bg-white opacity-0 opacity-25-hover transition-opacity" />
      )}
      
      {/* Content */}
      <span className="position-relative d-inline-flex align-items-center justify-content-center">
        {isLoading ? (
          <>
            <span className={`spinner-border spinner-border-sm ${iconSpacing}`} role="status" aria-hidden="true" />
            {t('common.loading')}
          </>
        ) : (
          children
        )}
      </span>

      {/* Shine effect for gradient variant */}
      {variant === 'gradient' && (
        <motion.span
          className="position-absolute top-0 start-0 w-100 h-100 bg-gradient-to-r from-transparent via-white-20 to-transparent"
          initial={{ x: isRTL ? '100%' : '-100%' }}
          whileHover={{ x: isRTL ? '-100%' : '100%' }}
          transition={{ duration: 0.6 }}
        />
      )}
    </motion.button>
  );
};
